import { Cell } from '@table-library/react-table-library/table';

import { comparisonColumns, contributorColumns } from '../GscTools/columns';
import { filterLabel, operatorLabel } from './groups';
import { inspectionLink, pageQueriesLink } from './rows';

/**
 * The columns this report defines on top of the shared GSC tool sets.
 *
 * The metric columns are always the shared ones, so a group row here and a page row anywhere else
 * in the app report the same numbers under the same headings; only the key columns, the overlap
 * column and the row actions are this report's own.
 */

/** A group's name: the key of block 02, which is the user's own label and not anything returned. */
function groupColumn(t) {
    return {
        label: t('tools.content_groups.comparison.group'),
        key: 'key',
        data: 'key',
        sortType: 'string',
        cellSize: 'minmax(180px, 1.6fr)',
        Component: Cell,
        renderCell: ({ data, csvDownload }) => (csvDownload ? data : <strong>{data}</strong>),
    };
}

/** Block 02 — each group, this window against its baseline. */
export function comparisonTableColumns(t, { hasPosition }) {
    const shared = comparisonColumns(t, {
        hasPosition,
        keyLabel: t('tools.content_groups.comparison.group'),
        isUrl: false,
    });

    return [groupColumn(t), ...shared.slice(1)];
}

/**
 * Block 01 — every applied rule, as it was written and as Search Console was sent it.
 *
 * Both forms are shown because they differ exactly where it matters: a prefix becomes an anchored
 * expression with every dot escaped, and seeing that escape is how a user knows the URL was
 * matched literally rather than as a pattern.
 */
export function definitionColumns(t) {
    return [
        {
            label: t('tools.content_groups.definition.group'),
            key: 'group',
            data: 'group',
            sortType: 'string',
            cellSize: 'minmax(140px, 1fr)',
            Component: Cell,
            renderCell: ({ data, csvDownload }) => (csvDownload ? data : <strong>{data}</strong>),
        },
        {
            label: t('tools.content_groups.definition.condition'),
            key: 'condition',
            data: 'condition',
            sortType: 'string',
            cellSize: 'minmax(150px, 1fr)',
            Component: Cell,
        },
        {
            label: t('tools.content_groups.definition.value'),
            key: 'value',
            data: 'value',
            sortType: 'string',
            cellSize: 'minmax(220px, 2fr)',
            Component: Cell,
        },
        {
            label: t('tools.content_groups.definition.sent'),
            key: 'sent',
            data: 'sent',
            sortType: 'string',
            cellSize: 'minmax(260px, 2.4fr)',
            Component: Cell,
            renderCell: ({ data, csvDownload }) =>
                csvDownload ? data : <code className="gsc-tool-expression">{data}</code>,
        },
    ];
}

export function definitionRows(t, scope) {
    return scope.rules.map((rule, index) => ({
        id: `${rule.group}-${rule.operator}-${rule.value}-${index}`,
        group: rule.group,
        condition: operatorLabel(t, rule.operator),
        value: rule.value,
        sent: filterLabel(rule),
    }));
}

/**
 * The other groups a URL also meets. It is a column rather than a footnote because the export
 * outlives the page: a file of one group's URLs that hid which of them are also in another group
 * would invite the two files to be added together.
 */
function alsoInColumn(t) {
    return {
        label: t('tools.content_groups.pages.also_in'),
        key: 'alsoIn',
        data: 'alsoIn',
        sortType: (array) =>
            array.sort((a, b) => (a.alsoIn?.length ?? 0) - (b.alsoIn?.length ?? 0)),
        cellSize: 'minmax(150px, 1.2fr)',
        Component: Cell,
        renderCell: ({ data, csvDownload }) => {
            if (data?.length) return data.join(', ');
            const none = t('tools.content_groups.pages.only_this');
            return csvDownload ? none : <span className="text-muted">{none}</span>;
        },
    };
}

/** Block 04 — the matching pages, with the shared metric columns and the overlap column. */
export function pageColumns(t, { hasPosition }) {
    return [
        ...contributorColumns(t, {
            hasPosition,
            keyLabel: t('tools.content_groups.pages.url'),
            isUrl: true,
        }),
        alsoInColumn(t),
    ];
}

/**
 * The row actions: the two tools that answer the next question about one exact URL.
 *
 * Both are links into another report rather than requests made from here. The page-filtered
 * detail opens Pages, Queries & Changes on that URL's queries; the inspection link only fills URL
 * Inspection's field, because an inspection spends quota and waits for the user to ask for it.
 * Each opens in its own tab so the groups on screen stay as they are. The column is left out of
 * the exports — a link is not a value.
 */
export function pageActionColumn(t, scope, period) {
    return {
        label: t('tools.content_groups.pages.open_in'),
        key: 'actions',
        data: 'key',
        sortType: (array) => array,
        cellSize: 'minmax(200px, 220px)',
        Component: Cell,
        renderCell: ({ item, csvDownload }) => {
            if (csvDownload) return '';

            return (
                <div className="gsc-tool-row-links">
                    <a
                        className="table-action-main-btn"
                        href={pageQueriesLink(scope, item.key, period)}
                        target="_blank"
                        rel="noreferrer"
                        title={t('tools.content_groups.pages.queries_title')}
                    >
                        {t('tools.content_groups.pages.queries')}
                    </a>
                    <a
                        className="table-action-main-btn"
                        href={inspectionLink(scope, item.key)}
                        target="_blank"
                        rel="noreferrer"
                        title={t('tools.content_groups.pages.inspect_title')}
                    >
                        {t('tools.content_groups.pages.inspect')}
                    </a>
                </div>
            );
        },
    };
}
